import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { GenericQueryOptions } from '.';
import type { DetailedUser, EditUser, UserGrid, UserPagedQuery, UserProperties } from '../dtos';
import type { UserRole } from '../dtos/User/UserRole';

const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_USER_API
const url = `${baseUrl}/${apiUrl}`

export const useSearchUsers = (search: UserPagedQuery, options?: GenericQueryOptions<UserGrid>) => {
  return useQuery({
    queryKey: ['users', search],
    queryFn: () => axios.post<UserGrid>(`${url}/Search`, search),
    ...options
  })
}

export const useGetCurrentUser = () => {
  return useQuery<AxiosResponse<DetailedUser>, AxiosError>({
    queryKey: ['user', 'current'],
    queryFn: () => axios.get<DetailedUser>(`${url}/GetCurrent`)
  })
}

export const useGetUser = (userId: string) => {
  return useQuery<AxiosResponse<DetailedUser>, AxiosError>({
    queryKey: ['user', userId],
    queryFn: () => axios.get<DetailedUser>(`${url}/GetById?userId=${userId}`)
  })
}

export const useGetUserProperties = (
  userId: string,
  options?: GenericQueryOptions<UserProperties>,
) => {
  return useQuery({
    queryKey: ['user', userId, 'properties'],
    queryFn: () => axios.get<UserProperties>(`${url}/GetUserProperties?userId=${userId}`),
    ...options
  })
}

export const useGetRoles = (userId: string | null) => {
  return useQuery({
    queryKey: ['user', userId, 'roles'],
    queryFn: () =>
      axios.get<{ role: UserRole; roleName: string }[]>(`${url}/GetRoles`, { params: { userId } })
  })
}

export const useGetUserTypes = () => {
  return useQuery({
    queryKey: ['userTypes'],
    queryFn: () => axios.get<{ id: number; itemName: string }[]>(`${url}/GetUserTypes`)
  })
}

export const useUpdateUser = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['user', userId],
    mutationFn: (user: EditUser) => axios.post(`${url}/UpdateUser`, user),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['user', variables.userId])
    }
  })
}

export const useCheckUserNameExists = (
  userName: string,
  options?: GenericQueryOptions<boolean>,
) => {
  return useQuery({
    queryKey: ['checkUserNameExists', userName],
    queryFn: () => axios.get<boolean>(`${url}/CheckUserNameExists`, { params: { userName } }),
    ...options,
  })
}

export const exportUrl = `${url}/Export`
